import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Hash, Braces, Binary, Key, Type, Clock, FileCode, Sparkles } from 'lucide-react';
import { getAllTools, type ToolDefinition } from '../lib/toolRegistry';
import { trackSearch } from '@devkit/analytics';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PaletteEntry {
  id: string;
  label: string;
  description: string;
  category: string;
  keywords: string;
  href: string;
  icon: typeof Hash;
}

function entryFromTool(tool: ToolDefinition): PaletteEntry {
  // Map category to a representative icon. Anything new gets Sparkles.
  const iconByCategory: Record<string, PaletteEntry['icon']> = {
    json: Braces,
    encoding: Binary,
    security: Key,
    regex: Type,
    time: Clock,
    text: FileCode,
    utility: Hash,
    web: Hash,
    code: Hash,
    ai: Sparkles,
  };
  return {
    id: tool.slug,
    label: tool.name,
    description: tool.shortDescription,
    category: tool.category,
    keywords: tool.keywords.join(' ').toLowerCase(),
    href: `/${tool.slug}`,
    icon: iconByCategory[tool.category] ?? Hash,
  };
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const entries: PaletteEntry[] = useMemo(
    () => getAllTools().map(entryFromTool),
    []
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return entries;
    const q = query.trim().toLowerCase();
    return entries.filter(
      (e) =>
        e.label.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q) ||
        e.keywords.includes(q)
    );
  }, [query, entries]);

  // Reset state whenever the dialog opens/closes.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // Defer focus so the dialog has mounted.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Reset active index on every query change so selection always points at row 0.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Track search once per query (after debounce-like idle).
  useEffect(() => {
    if (!open) return;
    if (!query.trim()) return;
    const t = setTimeout(() => {
      trackSearch(query.trim(), filtered.length);
    }, 400);
    return () => clearTimeout(t);
  }, [query, open, filtered.length]);

  const choose = useCallback(
    (entry: PaletteEntry) => {
      onOpenChange(false);
      navigate(entry.href);
    },
    [navigate, onOpenChange]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onOpenChange(false);
      return;
    }
    if (filtered.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(filtered[activeIndex]);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search tools"
      className="fixed inset-0 z-[1100] flex items-start justify-center pt-[12vh] px-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={() => onOpenChange(false)}
      onKeyDown={onKeyDown}
    >
      <div
        className="w-full max-w-xl bg-surface border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-700">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tools… (e.g. JSON, JWT, Base64)"
            className="flex-1 bg-transparent outline-none text-slate-100 placeholder:text-slate-500 text-sm"
            aria-label="Search query"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:inline-block text-xs font-mono px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
            esc
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-slate-500 text-sm">
              No tools match &ldquo;{query}&rdquo;.
            </div>
          ) : (
            <ul role="listbox" aria-label="Tools">
              {filtered.map((entry, idx) => {
                const Icon = entry.icon;
                const isActive = idx === activeIndex;
                return (
                  <li
                    key={entry.id}
                    role="option"
                    aria-selected={isActive}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => choose(entry)}
                    className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer ${
                      isActive ? 'bg-slate-700/60' : 'hover:bg-slate-700/30'
                    }`}
                  >
                    <span className="flex-shrink-0 w-8 h-8 rounded-md bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300">
                      <Icon className="w-4 h-4" aria-hidden />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-white truncate">{entry.label}</span>
                      <span className="block text-xs text-slate-400 truncate">{entry.description}</span>
                    </span>
                    <span className="hidden sm:block text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                      {entry.category}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-4 py-2 border-t border-slate-700 text-[11px] text-slate-500 flex items-center justify-between">
          <span>
            <kbd className="font-mono px-1 py-0.5 rounded bg-slate-800 border border-slate-700 mr-1">↑</kbd>
            <kbd className="font-mono px-1 py-0.5 rounded bg-slate-800 border border-slate-700 mr-2">↓</kbd>
            to navigate
          </span>
          <span>
            <kbd className="font-mono px-1 py-0.5 rounded bg-slate-800 border border-slate-700 mr-1">↵</kbd>
            to open
          </span>
        </div>
      </div>
    </div>
  );
}
